import { useMemo, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import {
  Award,
  CheckCircle2,
  Clock,
  Crown,
  Download,
  Flag,
  HelpCircle,
  Info,
  Lock,
  Medal,
  RotateCcw,
  Sparkles,
  TrendingUp,
  Trophy,
  Users,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime, formatDateOnly, formatNumber } from '../../lib/format'
import {
  isSelfOnlyBoard,
  myRankFor,
  rankForRow,
} from '../../lib/leaderboard-rank'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Avatar } from '../../components/Avatar'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { FilterRow } from '../../components/filters/FilterRow'
import { runExport } from '../../components/table/tableExport'
import { useAuthStore } from '../../stores/authStore'
import {
  leaderboardResponse,
  myGamificationStats,
} from '../../lib/types'

function isoFromDate(d) {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const da = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${da}`
}

/** Long-form definition of the "Completed by Priority" column, shown on
 *  hover. Names the Team Overview column it is routinely compared against,
 *  and states plainly that the two now share one rule. */
const COMPLETED_COLUMN_HINT =
  'Tasks completed inside the selected period, split by the task’s priority. A task counts here exactly when it counts in Team Overview’s “Done (all time)” — one rule over one set of rows — so widen the period to cover everything and the two agree. Points are measured separately and play no part in this count.'

const PERIODS = [
  { key: 'week', label: 'This week', days: 7 },
  { key: 'month', label: 'This month', days: 30 },
  // "This year" — from 1 January of the current year to now (handled
  // specially in the window calc below, not a rolling day count).
  { key: 'year', label: 'This year' },
]

const WORK_ROLE_LABEL = {
  manager: 'Manager',
  team_leader: 'Team Leader',
  employee: 'Employee',
}

// Column spec for the "Export" button on the Rankings board — mirrors the
// visible board (rank position, member, department, role, per-priority
// completions, points) so the CSV reads exactly like the table. Shared
// `runExport` gives the same BOM'd CSV every other panel table produces.
const LEADERBOARD_EXPORT_COLS = [
  { key: 'rank', header: 'Rank', exportValue: (r) => r.rank },
  { key: 'fullName', header: 'Member', exportValue: (r) => r.fullName ?? '' },
  { key: 'emailId', header: 'Email ID', exportValue: (r) => r.emailId ?? '' },
  {
    key: 'departmentName',
    header: 'Department',
    exportValue: (r) => r.departmentName ?? '',
  },
  {
    key: 'role',
    header: 'Role',
    exportValue: (r) => r.workRoleName ?? WORK_ROLE_LABEL[r.workRole] ?? 'Employee',
  },
  // "(this period)" on every one of these: the export filename stamps the
  // date the CSV was taken, not the window it covers, and a spreadsheet has
  // no period selector beside it — so without the suffix these read as
  // all-time totals the moment they leave the page.
  { key: 'urgent', header: 'Urgent completed (this period)', exportValue: (r) => r.completedByPriority?.urgent ?? 0 },
  { key: 'high', header: 'High completed (this period)', exportValue: (r) => r.completedByPriority?.high ?? 0 },
  { key: 'normal', header: 'Medium completed (this period)', exportValue: (r) => r.completedByPriority?.normal ?? 0 },
  { key: 'low', header: 'Low completed (this period)', exportValue: (r) => r.completedByPriority?.low ?? 0 },
  { key: 'points', header: 'Points', exportValue: (r) => r.points },
]

/** Tenant + department leaderboards plus the actor's own stats card.
 *  Visual sibling of Team Overview — flat header, KPI tile strip, then
 *  the ranked table. No dark gradient; the rest of the app is light, so
 *  this stops the page from feeling like it belongs to a different
 *  product. */
export function LeaderboardPage() {
  const user = useAuthStore((s) => s.user)
  // Default to This Year so the page lands on a populated, ranked view.
  // The rolling weekly / monthly windows can legitimately be empty (no
  // points awarded in the last 7 / 30 days), which made "My Rank" read
  // "Unranked" and "Players 0" on first load even for active users —
  // confusing. The year window reflects the actor's standing; they can
  // still narrow to a recent window.
  const [period, setPeriod] = useState('year')
  // Who can see EVERY department: Company Admin / Sub Admin and any
  // company-wide member (leaderboard.view_workspace). They get a
  // department FILTER dropdown. A department-scoped member
  // (leaderboard.view_department or none) only ever sees their own
  // department's board — the backend already limits the response, and we
  // lock the client to it too.
  const canSeeAllDepartments = useAuthStore(
    (s) =>
      s.user?.role === 'company_admin' ||
      s.user?.role === 'sub_admin' ||
      s.hasOperation('leaderboard.view_workspace'),
  )
  // Department filter — only used by company-wide actors. 'all' = every
  // department; otherwise a specific departmentId.
  const [deptFilter, setDeptFilter] = useState('all')
  // Top-level view toggle — flips the page between the live rankings
  // table (KPIs + leaderboard) and the static "How points work" guide
  // that explains the gain/lose-points rules. Users repeatedly asked
  // "where do my points come from?" so the answer now lives on the
  // same page as their score, one click away.
  const [view, setView] = useState('rankings')
  // Badges detail modal — opened from the Badges KPI card so members can see
  // which badges they've earned and their progress toward the locked ones.
  const [badgesOpen, setBadgesOpen] = useState(false)
  // Custom date-range filter (YYYY-MM-DD strings). When BOTH ends are set it
  // overrides the This week / month / Year presets, so members can view a
  // specific historical window. Picking a preset (or "Clear dates") drops it.
  const [customRange, setCustomRange] = useState({ from: '', to: '' })
  const isCustomRange = Boolean(customRange.from && customRange.to)
  // A reversed range (the "To" date before the "From" date) is invalid — we
  // surface an inline error and suppress results rather than silently swapping
  // the ends and showing data for a window the user didn't ask for.
  const rangeInvalid = isCustomRange && customRange.from > customRange.to
  // Member clicked on the board → opens a read-only detail view. Holds the
  // full leaderboard row (the board already carries everything the modal
  // shows), so there's no extra fetch and it works for any member the
  // viewer can see.

  const todayIso = useMemo(() => isoFromDate(new Date()), [])

  const window = useMemo(() => {
    // Custom range wins when both ends are set. A reversed range is handled by
    // `rangeInvalid` (query disabled + inline error), so no silent swap here.
    if (customRange.from && customRange.to) {
      return { from: customRange.from, to: customRange.to }
    }
    const now = new Date()
    let from
    if (period === 'year') {
      // From 1 January of the current year.
      from = new Date(now.getFullYear(), 0, 1)
    } else {
      const days = PERIODS.find((p) => p.key === period)?.days ?? 30
      from = new Date()
      from.setDate(from.getDate() - days)
    }
    return { from: isoFromDate(from), to: isoFromDate(now) }
  }, [period, customRange])

  const leaderboardQuery = useQuery({
    queryKey: ['leaderboard', window.from, window.to],
    queryFn: async () => {
      const params = { from: window.from, to: window.to }
      const res = await api.get('/gamification/leaderboard', { params })
      return leaderboardResponse.parse(res.data)
    },
    // Don't fetch (or show) results for an invalid reversed range.
    enabled: !rangeInvalid,
    // Keep the previous result mounted while a period switch fetches, so the
    // data never resets to `undefined` (which collapsed the whole board to a
    // one-line "Loading…" text and jumped the layout). `isPlaceholderData` then
    // tells us we're showing the OLD period's rows — we render a skeleton over
    // that window instead of flashing the wrong members in the podium.
    placeholderData: keepPreviousData,
  })

  // True while a period switch is in flight (first-ever load OR showing the
  // previous period's placeholder data). Drives the skeleton podium.
  const boardLoading =
    leaderboardQuery.isLoading || leaderboardQuery.isPlaceholderData

  const myStatsQuery = useQuery({
    queryKey: ['gamification-me'],
    queryFn: async () => {
      const res = await api.get('/gamification/me')
      return myGamificationStats.parse(res.data)
    },
  })

  const allRows = leaderboardQuery.data?.data ?? []
  let rows = allRows
  if (canSeeAllDepartments) {
    // Company-wide view — every department, optionally narrowed by the
    // department filter dropdown. (The backend returns all departments
    // for these actors.)
    if (deptFilter !== 'all') {
      rows = allRows.filter((r) => String(r.departmentId ?? '') === deptFilter)
    }
  }
  // NO second department filter for everyone else. The backend already
  // resolves the exact population a department-scoped member may see (their
  // whole department, minus company-wide members, Company Admins and Sub
  // Admins) and is the security boundary. Re-filtering it here by
  // `departmentId` silently DROPPED rows the server legitimately returned —
  // an external collaborator granted the department has no WorkspaceMember
  // row and so carries `departmentId: null`, so the board rendered 2 rows
  // where the API returned 3. One canonical visibility rule, server-side.

  // Is this a SELF-ONLY board? The backend sets `viewerPlayers` only when the
  // viewer's scope collapsed to just themselves (an Employee whose Work Role
  // carries no leaderboard operation, or a member with no workspace /
  // department). `viewerRank` is NOT a reliable signal — it is null for a
  // self-only viewer who has scored nothing yet.
  const selfOnlyBoard = isSelfOnlyBoard(leaderboardQuery.data)

  // Rank for the currently-displayed board.
  //
  // Normal (multi-row) board: the 1-based position within the points-sorted
  // rows. The server rows arrive sorted by points DESC and the department
  // filter above preserves that order, so `i + 1` is the correct standing —
  // and it makes a company-wide viewer's dropdown-filtered department view
  // rank identically to what a Manager / Team Leader of that department sees.
  //
  // SELF-ONLY board: positional rank is a LIE. The viewer is the only row, so
  // `i + 1` always yields #1 — which rendered a 0-point member as
  // "#1 · CHAMPION" and contradicted the "My Rank #2" card right above it.
  // Use the server's TRUE standing (`viewerRank`), which is measured against
  // the peer group the viewer is actually ranked within; null when they have
  // not scored, i.e. genuinely unranked.
  rows = rows.map((r, i) => ({
    ...r,
    rank: rankForRow(i, {
      selfOnly: selfOnlyBoard,
      viewerRank: leaderboardQuery.data?.viewerRank ?? null,
    }),
  }))

  // Department options for the company-wide filter — the distinct
  // departments present on the board.
  const deptOptions = useMemo(() => {
    const m = new Map()
    for (const r of leaderboardQuery.data?.data ?? []) {
      if (r.departmentId) {
        m.set(String(r.departmentId), r.departmentName ?? 'No department')
      }
    }
    return [...m.entries()].map(([value, label]) => ({ value, label }))
  }, [leaderboardQuery.data])

  const myRow = rows.find((r) => r.userId === user?.id) ?? null
  // Prefer the backend's TRUE standing (set only when the viewer's board
  // collapsed to self-only — an Employee who can't see teammates). Deriving
  // rank from the visible rows there would always yield #1 with 1 player.
  // For everyone else `viewerRank` is null and we fall back to the rows the
  // client already has (respecting any client-side department filter).
  const serverRank = leaderboardQuery.data?.viewerRank
  const serverPlayers = leaderboardQuery.data?.viewerPlayers
  // Use the actor's OWN row rank (the single source of truth computed above),
  // so "My Rank" is byte-identical to that user's row in the table/podium —
  // which now holds for a self-only board too, because the rows above take
  // their rank from `viewerRank` in that case.
  //
  // No positional fallback on a self-only board: `myRow.rank` would be the
  // array index (#1) and a viewer who has scored nothing is genuinely
  // UNRANKED, not the champion. Falling through to null renders "—".
  const myRank = myRankFor({
    serverRank,
    selfOnly: selfOnlyBoard,
    myRowRank: myRow?.rank ?? null,
  })
  // "My Points" tracks the SELECTED window + department scope so it
  // reconciles with "My Rank" / "Players". When the actor isn't in the
  // window's ranked (department) rows they earned nothing in it → 0.
  const myPoints = myRow?.points ?? 0
  const myBadgeCount = myStatsQuery.data?.badges.length ?? 0
  const totalPlayers = serverPlayers ?? rows.length

  const periodLabel = isCustomRange
    ? 'Custom range'
    : (PERIODS.find((p) => p.key === period)?.label ?? '')

  // Company Admins / Sub Admins reach this from their own panel (route
  // `/leaderboard` under AdminLayout); their Dashboard lives at
  // `/dashboard`. Employees use the `/app` home.
  const isAdminPanel =
    user?.role === 'company_admin' || user?.role === 'sub_admin'
  // Admin roles don't get a personal gamification panel on the Leaderboard:
  // the KPI strip (My Points / My Rank / Badges / Users) and
  // the "My recent points" list are hidden for Company Admin, Super Admin AND
  // Sub Admin, so the board reads as a pure team ranking for every admin.
  // Only regular members (the task-doers who actually earn points) see their
  // own stats. Company / Super Admins are excluded from the points ledger
  // server-side; Sub Admins are hidden here by product requirement (admins
  // manage the board, they don't compete on it).
  const showPersonalKpis =
    user?.role !== 'company_admin' &&
    user?.role !== 'super_admin' &&
    user?.role !== 'sub_admin'

  // Export the CURRENTLY-VISIBLE ranked board (respects the period + custom
  // range + department filter) to CSV via the shared exporter — the one
  // DataTable-kit feature that fits a bespoke ranked board. Ranks are the
  // position-based values already on the rows, so the CSV mirrors the table.
  function handleExport() {
    if (rows.length === 0) return
    runExport('CSV', rows, LEADERBOARD_EXPORT_COLS, {
      baseName: 'leaderboard',
      title: 'Leaderboard',
      isoDate: todayIso,
    })
  }

  return (
    <div>
      <Breadcrumb
        current="Leaderboard"
        homePath={isAdminPanel ? '/dashboard' : '/app'}
        homeLabel="Dashboard"
      />

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <Medal size={22} className="text-[color:var(--color-accent-strong)]" />
            Leaderboard
          </h1>
          <p className="mt-1 text-[color:var(--color-ink-muted)]">
            Earn points by finishing tasks.
          </p>
        </div>
      </div>

      {/* View tabs — Rankings (the live board) vs. How points work
       *  (the gain/lose-points guide). Tab pill style matches the
       *  Period / Scope toggles below for visual consistency. */}
      <div className="flex items-center gap-1 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-0.5 mb-4 w-fit">
        {[
          { value: 'rankings', label: 'Rankings', icon: Trophy },
          { value: 'rules', label: 'How points work', icon: HelpCircle },
        ].map((v) => (
          <button
            key={v.value}
            type="button"
            onClick={() => setView(v.value)}
            className={
              'h-9 px-4 rounded text-sm font-medium cursor-pointer inline-flex items-center gap-1.5 transition-colors ' +
              (view === v.value
                ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]'
                : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]')
            }
          >
            <v.icon size={14} />
            {v.label}
          </button>
        ))}
      </div>

      {view === 'rules' ? (
        <PointsRulesPanel settings={myStatsQuery.data?.settings} />
      ) : null}

      {view === 'rankings' ? (
        <>
      {/* Personal stats — same KPI tile shape as Team Overview / Dashboard.
       *  Hidden for Company / Super Admins (see `showPersonalKpis`), whose
       *  personal numbers are always empty because they don't earn points. */}
      {showPersonalKpis ? (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiTile
          icon={Trophy}
          label="My Points"
          value={formatNumber(myPoints)}
          tone="gold"
        />
        <KpiTile
          icon={TrendingUp}
          label="My Rank"
          value={myRank ? `#${myRank}` : '—'}
          // Rank is period-scoped, so the selected window (This week / month /
          // Year / Custom range) reads naturally here. Keep "Unranked" when the
          // member has no rank.
          hint={myRank ? periodLabel : 'Unranked'}
          tone={myRank ? 'sky' : 'slate'}
        />
        <KpiTile
          icon={Award}
          label="Badges"
          value={myBadgeCount}
          hint="View all →"
          tone="violet"
          onClick={() => setBadgesOpen(true)}
        />
        <KpiTile
          icon={Users}
          label="Users"
          value={totalPlayers}
          tone="emerald"
        />
      </div>
      ) : null}

      {/* Filters — time-window selector, plus a department filter for
       *  company-wide actors. Department-scoped members see only their own
       *  department (no filter). */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-1 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-0.5">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => {
                setPeriod(p.key)
                // A preset supersedes any custom range the user had set.
                setCustomRange({ from: '', to: '' })
              }}
              className={
                'h-8 px-3 rounded text-sm font-medium cursor-pointer transition-colors ' +
                (!isCustomRange && period === p.key
                  ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]'
                  : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]')
              }
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Custom historical range — overrides the presets once both ends
         *  are set. Reuses the shared FilterRow date field. */}
        <FilterRow.DateField
          label="From"
          value={customRange.from}
          onChange={(v) => setCustomRange((r) => ({ ...r, from: v }))}
          max={customRange.to || todayIso}
        />
        <FilterRow.DateField
          label="To"
          value={customRange.to}
          onChange={(v) => setCustomRange((r) => ({ ...r, to: v }))}
          min={customRange.from || undefined}
          max={todayIso}
        />
        {/* Show Clear once EITHER end is set (not only when both are), so a
         *  half-entered range can still be cleared — matches the Goals page. */}
        {customRange.from || customRange.to ? (
          <button
            type="button"
            onClick={() => setCustomRange({ from: '', to: '' })}
            className="text-xs font-medium text-[color:var(--color-accent-strong)] hover:underline cursor-pointer"
          >
            Clear Dates
          </button>
        ) : null}
        {rangeInvalid ? (
          <p role="alert" className="w-full text-xs text-[color:var(--color-priority-urgent)]">
            The “To” date must be on or after the “From” date.
          </p>
        ) : null}
        {/* Right-aligned group — Department filter (company-wide actors only)
         *  + Export. `ml-auto` on the group pushes both to the trailing edge
         *  regardless of whether the Department select is present. */}
        <div className="ml-auto flex items-center gap-2">
          {canSeeAllDepartments ? (
            <select
              aria-label="Department"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              className="h-9 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] cursor-pointer"
            >
              <option value="all">All departments</option>
              {deptOptions.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          ) : null}
          {/* Export the visible ranked board to CSV — the one DataTable-kit
           *  feature that fits this bespoke podium/rank surface. */}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExport}
            disabled={rows.length === 0}
            title={
              rows.length
                ? `Export ${rows.length} ${rows.length === 1 ? 'row' : 'rows'} to CSV`
                : 'Nothing to export'
            }
          >
            <Download size={14} />
            Export
          </Button>
        </div>
      </div>

      {/* Leaderboard body */}
      {rangeInvalid ? (
        <div className="rounded-2xl border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-12 text-center">
          <p className="text-base font-semibold text-[color:var(--color-ink-strong)]">
            Invalid date range
          </p>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            The “To” date must be on or after the “From” date. Adjust the dates to see the leaderboard.
          </p>
        </div>
      ) : boardLoading ? (
        // Skeleton podium — keeps the layout stable and shows NO data while a
        // period switch loads, so the runner-up / third cards never flash the
        // previous period's members before the new data settles.
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 sm:items-end p-4 border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
            {[
              { order: 'sm:order-1', pad: 'py-5', med: 'h-11 w-11' },
              { order: 'sm:order-2', pad: 'py-6', med: 'h-16 w-16' },
              { order: 'sm:order-3', pad: 'py-5', med: 'h-11 w-11' },
            ].map((s, i) => (
              <div
                key={i}
                className={`${s.order} ${s.pad} flex flex-col items-center rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-4 animate-pulse`}
              >
                <div className={`${s.med} rounded-full bg-[color:var(--color-canvas)]`} />
                <div className="mt-3 h-4 w-20 rounded bg-[color:var(--color-canvas)]" />
                <div className="mt-2 h-3 w-24 rounded bg-[color:var(--color-canvas)]" />
                <div className="mt-3 h-5 w-12 rounded bg-[color:var(--color-canvas)]" />
              </div>
            ))}
          </div>
        </div>
      ) : leaderboardQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(leaderboardQuery.error).message}
        </p>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-12 text-center">
          <span className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10 mb-3">
            <Trophy size={24} />
          </span>
          <p className="text-base font-semibold text-[color:var(--color-ink-strong)]">
            No points awarded yet
          </p>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)] max-w-md mx-auto">
            Complete tasks to start earning points. The first completion
            lights up the leaderboard.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
          {/* Podium — top 3 as separated cards. `sm:items-end` bottom-
           *  aligns them so the taller CHAMPION card rises above #2 / #3
           *  (a raised-podium hierarchy); `p-4` gives the elevated card
           *  room so its shadow isn't clipped by the container. */}
          {/* Never on a SELF-ONLY board: the viewer is the only row, so the
            *  podium crowned them "#1 · CHAMPION" regardless of their real
            *  standing (a 0-point member included). Their true position is
            *  already on the "My Rank" card, measured against the peers they
            *  are actually ranked among. */}
          {!selfOnlyBoard && rows.slice(0, 3).length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 sm:items-end p-4 border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
              {rows.slice(0, 3).map((r, i) => (
                <PodiumCard
                  key={r.userId}
                  row={r}
                  place={i + 1}
                  highlight={r.userId === user?.id}
                />
              ))}
            </div>
          ) : null}

          {/* The board keeps its fixed column widths; on narrow (mobile)
           *  viewports it scrolls horizontally instead of clipping columns
           *  off-screen. `min-w-[980px]` holds the grid so headers stay aligned
           *  over their columns; `lg:min-w-0` drops the floor on desktop. The
           *  podium above stays full-width (outside this scroller). */}
          <div className="overflow-x-auto">
          <div className="min-w-[980px] lg:min-w-0">
          {/* Header */}
          <div className="grid grid-cols-[73px_281px_171px_141px_minmax(0,1fr)_101px] items-center px-4 py-3 gap-3 bg-[color:var(--color-canvas)] border-b border-[color:var(--color-rule)] text-sm font-semibold text-[color:var(--color-ink-muted)]">
            <span>Rank</span>
            <span>Member</span>
            <span>Department</span>
            <span>Role</span>
            {/* Two lines on purpose. The bare "Completed by priority" read as
             *  an all-time total, so it got compared against Team Overview's
             *  "Done (all time)" and did not match (QA 2026-09-12). Both columns
             *  now count the same thing — a task row carrying a completedAt, over
             *  the same feed — so the only thing left between them is the window,
             *  and the caption names it. Points no longer gate this column.
             *
             *  Kept as a caption rather than a longer single line because this
             *  column is `minmax(0,1fr)` and bottoms out near 153px at the
             *  grid's 980px floor, where one long line would wrap anyway. */}
            <span className="leading-tight" title={COMPLETED_COLUMN_HINT}>
              Completed by Priority
              <span className="block text-xs font-normal text-[color:var(--color-ink-subtle)]">
                Completed tasks, this period
              </span>
            </span>
            <span className="text-right">Points</span>
          </div>

          {rows.map((r, i) => (
            <LeaderRow
              key={r.userId}
              row={r}
              highlight={r.userId === user?.id}
              index={i}
            />
          ))}
          </div>
          </div>
        </div>
      )}

      {/* Your own recent point awards, shown directly under the board so a
       *  member sees their history from the Rankings tab (where they check
       *  their standing) rather than having to open "How points work".
       *  Hidden for Company / Super Admins (see `showPersonalKpis`) — they
       *  don't earn points, so this is always the empty "No point awards
       *  yet" state for them (same reason the KPI strip is hidden). */}
      {showPersonalKpis ? (
      <div className="mt-6">
        <MyRecentPointsPanel recent={myStatsQuery.data?.recent ?? []} />
      </div>
      ) : null}
        </>
      ) : null}

      <BadgesModal
        open={badgesOpen}
        onClose={() => setBadgesOpen(false)}
        catalogue={myStatsQuery.data?.badgeCatalogue ?? []}
        earned={myStatsQuery.data?.badges ?? []}
        creditedTaskCount={myStatsQuery.data?.creditedTaskCount ?? 0}
      />
    </div>
  )
}

/** Badges detail — opened from the Badges KPI card. Lists every badge in
 *  the tenant's catalogue as EARNED (✓ + award date) or LOCKED (a progress
 *  bar toward `requiredTaskCount` delivered tasks), so members can see what
 *  they've earned and what's next — the tracking the card alone lacked. */
function BadgesModal({ open, onClose, catalogue, earned, creditedTaskCount }) {
  // Award date by badge code, for the earned rows.
  const earnedByCode = useMemo(() => {
    const m = new Map()
    for (const b of earned) m.set(b.badgeCode, b.awardedAt)
    return m
  }, [earned])
  // Read the catalogue as a ladder: lowest threshold first.
  const ladder = useMemo(
    () =>
      [...catalogue].sort(
        (a, b) =>
          a.requiredTaskCount - b.requiredTaskCount ||
          (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
      ),
    [catalogue],
  )
  const earnedCount = ladder.filter((b) => earnedByCode.has(b.code)).length

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Your Badges"
      description={
        ladder.length
          ? `${earnedCount} of ${ladder.length} earned · ${creditedTaskCount} ${creditedTaskCount === 1 ? 'task' : 'tasks'} delivered`
          : undefined
      }
      size="lg"
    >
      {ladder.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] m-0">
          This company has no badges configured.
        </p>
      ) : (
        <ul className="flex flex-col gap-2 m-0 p-0 list-none">
          {ladder.map((b) => {
            const awardedAt = earnedByCode.get(b.code)
            const isEarned = Boolean(awardedAt)
            const progress = Math.min(creditedTaskCount, b.requiredTaskCount)
            const pct = b.requiredTaskCount
              ? Math.round((progress / b.requiredTaskCount) * 100)
              : 0
            const remaining = Math.max(
              b.requiredTaskCount - creditedTaskCount,
              0,
            )
            return (
              <li
                key={b.code}
                className={
                  'rounded-xl border p-4 flex items-start gap-3 ' +
                  (isEarned
                    ? 'border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)]'
                    : 'border-[color:var(--color-rule)] bg-[color:var(--color-card)]')
                }
              >
                <div
                  className={
                    'h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ' +
                    (isEarned
                      ? 'bg-[#8b5cf6]/15 text-[#8b5cf6] border border-[#8b5cf6]/30'
                      : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-subtle)] border border-[color:var(--color-rule)]')
                  }
                >
                  {isEarned ? <Award size={20} /> : <Lock size={18} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="m-0 text-sm font-semibold text-[color:var(--color-ink-strong)]">
                      {b.name}
                    </p>
                    {isEarned ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-[color:var(--color-success)]">
                        <CheckCircle2 size={13} /> Earned
                      </span>
                    ) : null}
                  </div>
                  {b.description ? (
                    <p className="mt-0.5 m-0 text-xs text-[color:var(--color-ink-muted)]">
                      {b.description}
                    </p>
                  ) : null}
                  {isEarned ? (
                    <p className="mt-1 m-0 text-xs text-[color:var(--color-ink-muted)]">
                      Earned on {formatDateOnly(awardedAt)}
                    </p>
                  ) : (
                    <div className="mt-2">
                      <div className="h-1.5 rounded-full bg-[color:var(--color-canvas)] overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[color:var(--color-accent)]"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="mt-1 m-0 text-xs text-[color:var(--color-ink-muted)] tabular-nums">
                        {progress} / {b.requiredTaskCount} tasks
                        {remaining > 0 ? ` · ${remaining} to go` : ' · unlocking soon'}
                      </p>
                    </div>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </Modal>
  )
}

// ── Points rules guide ──────────────────────────────────────────────
//
// Display metadata (label / tone / blurb) plus FALLBACK point values
// mirroring the backend DEFAULT_SETTINGS. The LIVE per-tenant numbers
// now arrive on the `/gamification/me` response (`settings`) — readable
// by every member, not just Company Admins — and `PointsRulesPanel`
// merges them over these constants. These defaults only show until that
// response lands (or for older API builds without the field), so an
// admin-tuned Urgent = 40 is reflected here, not the hardcoded 60.

const PRIORITY_POINTS = [
  { key: 'urgent', label: 'Urgent', points: 60, tone: 'bg-red-100 text-red-700 border-red-200 dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10' },
  { key: 'high', label: 'High', points: 40, tone: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10' },
  { key: 'normal', label: 'Medium', points: 20, tone: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10' },
  { key: 'low', label: 'Low', points: 10, tone: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-white/[0.06] dark:text-slate-300 dark:border-white/10' },
]

const TIME_MULTIPLIERS = [
  {
    key: 'early',
    label: 'Early',
    percent: 100,
    blurb: 'Finished BEFORE the due date',
    tone: 'text-emerald-700',
  },
  {
    key: 'onTime',
    label: 'On time',
    percent: 100,
    blurb: 'Finished ON the due date (or no due date set)',
    tone: 'text-emerald-700',
  },
  {
    key: 'slightlyLate',
    label: 'Slightly late',
    percent: 60,
    blurb: '1–2 days past the due date',
    tone: 'text-amber-700',
  },
  {
    key: 'veryLate',
    label: 'Very late',
    percent: 30,
    blurb: '3 or more days past the due date',
    tone: 'text-red-700',
  },
]

const BADGE_TIERS = [
  { count: 1, name: 'First Win' },
  { count: 10, name: 'Ten Strong' },
  { count: 50, name: 'Fifty Up' },
  { count: 100, name: 'Centurion' },
]

/** Per-row reason → friendly label. The backend stores the reason
 *  string (`task.completed.on_time`, `task.completed.early`, …); we
 *  translate to a short human-readable phrase for the table column. */
const REASON_LABEL = {
  'task.completed.early': 'Early',
  'task.completed.on_time': 'On time',
  'task.completed.no_due_date': 'On time (no due date)',
  'task.completed.late': 'Slightly late',
  'task.completed.very_late': 'Very late',
  'task.reopened': 'Reopened (reversed)',
  'task.reopen_reversal': 'Reopened (reversed)',
  approval: 'Approval bonus',
  // Report-submission awards aren't tied to a task — the "Reason" reads
  // "Submitted"; the "Task" column shows which report (see NON_TASK_LABEL).
  'report.submitted.daily': 'Submitted',
  'report.submitted.weekly': 'Submitted',
  'report.submitted.monthly': 'Submitted',
}

/** Points awards that have NO task — report submissions. The "Task" column
 *  shows WHAT the award was for instead of "(deleted task)", which wrongly
 *  implied a task once existed and was removed. */
const NON_TASK_LABEL = {
  'report.submitted.daily': 'Daily report',
  'report.submitted.weekly': 'Weekly report',
  'report.submitted.monthly': 'Monthly report',
}

/** "My recent points" — per-task ledger feed showing each award the
 *  user actually earned. Rendered under the leaderboard in the Rankings
 *  tab so a member sees their own history right where they check their
 *  standing (it used to live under "How points work", where testers read
 *  it as a demo rather than their real data). Falls back to a friendly
 *  empty state when the ledger is empty. */
function MyRecentPointsPanel({ recent }) {
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 mb-4">
      <div className="flex items-center gap-2 mb-1">
        <Trophy size={16} className="text-[color:var(--color-accent-strong)]" />
        <h3 className="m-0 text-[color:var(--color-ink-strong)]">
          My recent points
        </h3>
      </div>
      <p className="text-sm text-[color:var(--color-ink-muted)] mb-4">
        Your last {recent.length || 'few'} point awards — the task, the reason,
        and how many points each one was worth.
      </p>
      {recent.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-6 text-center">
          <p className="text-sm text-[color:var(--color-ink-muted)] m-0">
            No point awards yet. Finish a task assigned to you and the first
            row will land here.
          </p>
        </div>
      ) : (
        // `max-h-[320px] overflow-y-auto` caps the visible area at ~6
        // rows so the table doesn't push the rules panel below the fold.
        // Anything beyond scrolls vertically with a thin native bar.
        // Rows are already sorted newest-first server-side
        // (PointsLedgerEntry sort: { awardedAt: -1 }) so the most recent
        // award is always at the top.
        <div className="rounded-lg border border-[color:var(--color-rule)] overflow-hidden">
          <div className="max-h-[320px] overflow-auto">
          <table className="w-full text-left text-sm border-separate border-spacing-0">
            {/* `sticky top-0` on each <th> keeps the header pinned to
             *  the visible top edge while rows scroll under it. bg-canvas
             *  fills the cell so scrolled rows don't bleed through.
             *  z-10 keeps it above the body. */}
            <thead>
              <tr className="text-[color:var(--color-ink-muted)]">
                <th className="px-4 py-2 font-medium border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] sticky top-0 z-10">
                  Task
                </th>
                <th className="px-4 py-2 font-medium border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] sticky top-0 z-10 w-[110px] whitespace-nowrap">
                  Deadline
                </th>
                <th className="px-4 py-2 font-medium border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] sticky top-0 z-10 w-[110px] whitespace-nowrap">
                  Completed
                </th>
                <th className="px-4 py-2 font-medium border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] sticky top-0 z-10 w-[110px] whitespace-nowrap">
                  Assigned
                </th>
                <th className="px-4 py-2 font-medium border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] sticky top-0 z-10 w-[180px]">
                  Reason
                </th>
                <th className="px-4 py-2 font-medium border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] sticky top-0 z-10 w-[120px]">
                  Date
                </th>
                <th className="px-4 py-2 font-medium border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] sticky top-0 z-10 w-[100px] text-right">
                  Points
                </th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => {
                const isNegative = r.points < 0
                return (
                  <tr
                    key={r.id}
                    className="border-t border-[color:var(--color-rule-subtle)] hover:bg-[color:var(--color-canvas)]"
                  >
                    <td className="px-4 py-2 border-b border-[color:var(--color-rule-subtle)] text-[color:var(--color-ink)]">
                      {r.taskName ?? NON_TASK_LABEL[r.reason] ?? (
                        <span className="italic text-[color:var(--color-ink-muted)]">
                          (deleted task)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 border-b border-[color:var(--color-rule-subtle)] text-[color:var(--color-ink-muted)] tabular-nums whitespace-nowrap">
                      {r.taskDueDate ? formatDateOnly(r.taskDueDate) : '—'}
                    </td>
                    <td className="px-4 py-2 border-b border-[color:var(--color-rule-subtle)] text-[color:var(--color-ink-muted)] tabular-nums whitespace-nowrap">
                      {r.taskCompletedAt ? formatDateOnly(r.taskCompletedAt) : '—'}
                    </td>
                    <td className="px-4 py-2 border-b border-[color:var(--color-rule-subtle)] text-[color:var(--color-ink-muted)] tabular-nums whitespace-nowrap">
                      {r.taskAssignedAt ? formatDateOnly(r.taskAssignedAt) : '—'}
                    </td>
                    <td className="px-4 py-2 border-b border-[color:var(--color-rule-subtle)] text-[color:var(--color-ink-muted)]">
                      {REASON_LABEL[r.reason] ?? r.reason}
                    </td>
                    <td className="px-4 py-2 border-b border-[color:var(--color-rule-subtle)] text-[color:var(--color-ink-muted)] tabular-nums">
                      {formatDateTime(r.awardedAt)}
                    </td>
                    <td
                      className={
                        'px-4 py-2 border-b border-[color:var(--color-rule-subtle)] text-right font-semibold tabular-nums ' +
                        (isNegative ? 'text-red-700' : 'text-emerald-700')
                      }
                    >
                      {isNegative ? '' : '+'}
                      {r.points}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  )
}

/** Static, static-everywhere guide that explains the points system.
 *  Renders when the user clicks the "How points work" tab. Has three
 *  cards stacked top-to-bottom:
 *    1. Base points by priority (one row per priority)
 *    2. Time multipliers (early / on-time / slightly late / very late)
 *    3. How to LOSE points + badge milestones
 *  Plus a worked-example block so users can see the formula end-to-end. */
function PointsRulesPanel({ settings }) {
  // Show the tenant's CONFIGURED scoring (from /gamification/me), falling
  // back to the platform defaults until it loads. Merges the live values
  // over the static display metadata (tone / label / blurb).
  const pp = settings?.priorityPoints
  const tm = settings?.timeMultipliers
  const priorityRows = PRIORITY_POINTS.map((p) => ({
    ...p,
    points: pp?.[p.key] ?? p.points,
  }))
  const timeRows = TIME_MULTIPLIERS.map((m) => ({
    ...m,
    percent: tm?.[m.key] ?? m.percent,
  }))
  const ptOf = (key) => priorityRows.find((p) => p.key === key)?.points ?? 0
  const mxOf = (key) => timeRows.find((m) => m.key === key)?.percent ?? 100
  const pts = (base, percent) => Math.round((base * percent) / 100)
  return (
    <div className="space-y-4 mb-6">
      {/* Intro callout */}
      <div className="rounded-xl border border-[color:var(--color-info-border)] bg-[color:var(--color-info-bg)] p-4 flex items-start gap-3">
        <span className="h-8 w-8 rounded-md bg-[color:var(--color-info)]/15 text-[color:var(--color-info)] flex items-center justify-center shrink-0">
          <Info size={16} />
        </span>
        <div className="text-sm text-[color:var(--color-ink)]">
          {/* Heading element so the global h1-h6 rule renders this at
           *  body+1px instead of inheriting the body size. */}
          <h3 className="m-0 text-[color:var(--color-ink-strong)]">
            Points reward delivery
          </h3>
          <p className="mt-1 m-0 text-[color:var(--color-ink-muted)]">
            You earn points every time a task assigned to you moves into a
            completion status. How many you earn depends on the task&apos;s{' '}
            <strong>priority</strong> and <strong>how close to the due date</strong>{' '}
            you finished it. The figures below are your company&apos;s
            current settings, configured by your Company Admin on the
            Gamification settings page.
          </p>
        </div>
      </div>

      {/* Card 1 — Base points by priority */}
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Flag size={16} className="text-[color:var(--color-accent-strong)]" />
          <h3 className="font-semibold text-[color:var(--color-ink-strong)] m-0">
            Base points by priority
          </h3>
        </div>
        <p className="text-sm text-[color:var(--color-ink-muted)] mb-4">
          A task&apos;s priority sets the BASE point value. Higher priority =
          more points.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {priorityRows.map((p) => (
            <div
              key={p.key}
              className={`rounded-lg border ${p.tone} p-4 flex items-center justify-between`}
            >
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold m-0 opacity-80">
                  {p.label}
                </p>
                <p className="mt-1 m-0 text-2xl font-display font-semibold tabular-nums">
                  {p.points}
                </p>
              </div>
              <span className="text-xs font-medium opacity-70">pts</span>
            </div>
          ))}
        </div>
      </div>

      {/* Card 2 — Time multipliers */}
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={16} className="text-[color:var(--color-accent-strong)]" />
          <h3 className="font-semibold text-[color:var(--color-ink-strong)] m-0">
            Timing multipliers
          </h3>
        </div>
        <p className="text-sm text-[color:var(--color-ink-muted)] mb-4">
          Your base points are multiplied by a percentage depending on WHEN you
          finish vs. the due date.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {timeRows.map((m) => (
            <div
              key={m.key}
              className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-4"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                  {m.label}
                </span>
                <span className={`text-lg font-display font-semibold tabular-nums ${m.tone}`}>
                  ×{m.percent}%
                </span>
              </div>
              <p className="mt-1 text-xs text-[color:var(--color-ink-muted)] m-0">
                {m.blurb}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Card 3 — Worked examples */}
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={16} className="text-[color:var(--color-accent-strong)]" />
          <h3 className="font-semibold text-[color:var(--color-ink-strong)] m-0">
            Worked examples
          </h3>
        </div>
        <ul className="space-y-3 text-sm text-[color:var(--color-ink)]">
          <li className="flex items-start gap-2">
            <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
            <span>
              Complete an <strong>Urgent</strong> task on time →{' '}
              <strong className="tabular-nums">
                {ptOf('urgent')} × {mxOf('onTime')}% ={' '}
                {pts(ptOf('urgent'), mxOf('onTime'))} points
              </strong>
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
            <span>
              Complete a <strong>Normal</strong> task 2 days late →{' '}
              <strong className="tabular-nums">
                {ptOf('normal')} × {mxOf('slightlyLate')}% ={' '}
                {pts(ptOf('normal'), mxOf('slightlyLate'))} points
              </strong>
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
            <span>
              Complete a <strong>High</strong> task 5 days late →{' '}
              <strong className="tabular-nums">
                {ptOf('high')} × {mxOf('veryLate')}% ={' '}
                {pts(ptOf('high'), mxOf('veryLate'))} points
              </strong>
            </span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
            <span>
              Complete a <strong>Low</strong> task early →{' '}
              <strong className="tabular-nums">
                {ptOf('low')} × {mxOf('early')}% ={' '}
                {pts(ptOf('low'), mxOf('early'))} points
              </strong>
            </span>
          </li>
        </ul>
      </div>

      {/* Card 4 — How to lose points */}
      <div className="rounded-xl border border-red-200 bg-red-50 dark:border-rose-500/20 dark:bg-rose-500/[0.07] p-5">
        <div className="flex items-center gap-2 mb-4">
          <RotateCcw size={16} className="text-red-700 dark:text-rose-400" />
          <h3 className="font-semibold text-red-900 dark:text-rose-200 m-0">
            How you LOSE points
          </h3>
        </div>
        <ul className="space-y-2 text-sm text-red-900 dark:text-rose-200/90">
          <li className="flex items-start gap-2">
            <span className="text-red-700 dark:text-rose-400 mt-0.5">•</span>
            <span>
              If a completed task is <strong>reopened</strong> (moved from a
              Done status back to an active one), the points you earned for
              that task are <strong>reversed</strong>. Your total drops by the
              same amount that was awarded.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-700 dark:text-rose-400 mt-0.5">•</span>
            <span>
              When the task is completed again, fresh points are awarded based
              on the priority + timing AT that second completion (which may be
              higher or lower than the first).
            </span>
          </li>
        </ul>
      </div>

      {/* Card 5 — Badges */}
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Award size={16} className="text-[color:var(--color-accent-strong)]" />
          <h3 className="font-semibold text-[color:var(--color-ink-strong)] m-0">
            Milestone badges
          </h3>
        </div>
        <p className="text-sm text-[color:var(--color-ink-muted)] mb-4">
          Hit these task-completion milestones to unlock badges. Badges count
          completed tasks — not points — so even Low-priority deliveries earn
          you progress.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {BADGE_TIERS.map((b) => (
            <div
              key={b.count}
              className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-3 text-center"
            >
              <span className="inline-flex h-10 w-10 rounded-xl bg-[#8b5cf6]/15 text-[#8b5cf6] border border-[#8b5cf6]/30 items-center justify-center mb-2">
                <Award size={18} />
              </span>
              <p className="text-base font-display font-semibold text-[color:var(--color-ink-strong)] m-0 tabular-nums">
                {b.count} {b.count === 1 ? 'task' : 'tasks'}
              </p>
              <p className="text-xs text-[color:var(--color-ink-muted)] mt-0.5 m-0">
                {b.name}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const TILE_TONE = {
  gold:
    'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]',
  sky:
    'bg-[color:var(--color-info-bg)] text-[color:var(--color-info)] border border-[color:var(--color-info-border)]',
  emerald:
    'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]',
  violet: 'bg-[#8b5cf6]/15 text-[#8b5cf6] border border-[#8b5cf6]/30',
  rose: 'bg-[#f43f5e]/15 text-[#f43f5e] border border-[#f43f5e]/30',
  slate:
    'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]',
}

function KpiTile({ icon: Icon, label, value, hint, tone, onClick }) {
  const clickable = typeof onClick === 'function'
  return (
    <div
      className={
        'rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 ' +
        (clickable
          ? 'cursor-pointer hover:border-[color:var(--color-accent)] transition-colors focus:outline-none focus-visible:border-[color:var(--color-accent)]'
          : '')
      }
      {...(clickable
        ? {
            role: 'button',
            tabIndex: 0,
            onClick,
            onKeyDown: (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            },
          }
        : {})}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] m-0">
            {label}
          </h3>
          <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums">
            {value}
          </p>
          {hint ? (
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              {hint}
            </p>
          ) : null}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            (TILE_TONE[tone] ?? TILE_TONE.slate)
          }
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

// Gold / Silver / Bronze for the top three. Kept in sync with the table's
// `rankIcon` below so the podium medal and the row medal read the same metal.
const PODIUM_TONE = {
  1: {
    medal: '#D4AF37', // gold
    badge:
      'bg-[#D4AF37]/15 text-[color:var(--color-medal-gold-text)] border border-[#D4AF37]/40',
    label: 'Champion',
  },
  2: {
    medal: '#AEB4BE', // silver
    badge:
      'bg-[#AEB4BE]/20 text-[color:var(--color-medal-silver-text)] border border-[#AEB4BE]/50',
    label: 'Runner-up',
  },
  3: {
    medal: '#CD7F32', // bronze
    badge:
      'bg-[#CD7F32]/15 text-[color:var(--color-medal-bronze-text)] border border-[#CD7F32]/40',
    label: 'Third place',
  },
}

function PodiumCard({ row, place, highlight }) {
  // `place` is the card's podium position (1/2/3 by points) — what should
  // drive the medal, label and centring. More robust than the backend
  // `row.rank`, which can be a non-1 value once a department filter
  // narrows the board. Falls back to `row.rank` if a caller omits it.
  const rank = place ?? row.rank
  const tone = PODIUM_TONE[rank] ?? PODIUM_TONE[3]
  const Icon = rank === 1 ? Crown : Medal
  // Olympic-style podium on sm+: runner-up LEFT, CHAMPION CENTRE, third
  // RIGHT. The DOM stays in 1-2-3 order (mobile stacks correctly and the
  // reading order is right); CSS `order` only reshuffles the desktop grid
  // columns so #1 lands in the middle.
  const orderClass =
    rank === 1 ? 'sm:order-2' : rank === 2 ? 'sm:order-1' : 'sm:order-3'
  // The champion card is the visual anchor: larger, gold-tinted, elevated,
  // and (via the container's `items-end`) taller so it rises above #2/#3.
  const isChampion = rank === 1
  return (
    <div
      className={
        orderClass +
        ' relative flex flex-col items-center text-center rounded-xl border ' +
        (isChampion ? 'px-4 py-6 shadow-lg ' : 'px-4 py-5 shadow-sm ') +
        (highlight ? 'ring-2 ring-[color:var(--color-accent)] ' : '')
      }
      style={
        isChampion
          ? {
              background: `linear-gradient(180deg, ${tone.medal}26, var(--color-card))`,
              borderColor: `${tone.medal}80`,
            }
          : { background: 'var(--color-card)', borderColor: 'var(--color-rule)' }
      }
    >
      {/* Medal — a larger crown for the champion sets the anchor. */}
      <span
        className={
          'rounded-full inline-flex items-center justify-center shrink-0 ' +
          (isChampion ? 'h-16 w-16' : 'h-11 w-11')
        }
        style={{
          backgroundColor: `${tone.medal}22`,
          color: tone.medal,
          border: `2px solid ${tone.medal}80`,
        }}
        aria-hidden="true"
      >
        <Icon size={isChampion ? 30 : 20} />
      </span>
      {/* Rank + label pill */}
      <span
        className={
          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold mt-3 ' +
          (isChampion ? 'text-xs' : 'text-[11px]') +
          ' ' +
          tone.badge
        }
      >
        #{rank} · {tone.label}
      </span>
      {/* Name */}
      <p
        className={
          'mt-2 w-full truncate font-semibold text-[color:var(--color-ink-strong)] ' +
          (isChampion ? 'text-base' : 'text-sm')
        }
      >
        {row.fullName}
        {highlight ? (
          <span className="ml-1 text-[12px] font-semibold text-[color:var(--color-accent-strong)]">
            · You
          </span>
        ) : null}
      </p>
      {/* Department */}
      <p className="w-full truncate text-xs text-[color:var(--color-ink-muted)]">
        {row.departmentName ?? '—'}
      </p>
      {/* Points — the largest figure on the champion card. */}
      <span
        className={
          'mt-2 inline-flex items-center gap-1 font-display tabular-nums text-[color:var(--color-ink-strong)] ' +
          (isChampion ? 'text-3xl' : 'text-xl')
        }
      >
        <Sparkles
          size={isChampion ? 18 : 14}
          className="text-[color:var(--color-accent-strong)]"
        />
        {formatNumber(row.points)}
      </span>
    </div>
  )
}

// Priority buckets shown in the "Completed by Priority" column, ordered
// most-urgent first. Colours come from the shared priority tokens.
const PRIORITY_ORDER = [
  { key: 'urgent', label: 'Urgent' },
  { key: 'high', label: 'High' },
  { key: 'normal', label: 'Medium' },
  { key: 'low', label: 'Low' },
]

function LeaderRow({ row, highlight, index }) {
  // Gold / Silver / Bronze — same metals as the podium's PODIUM_TONE above.
  const rankIcon =
    row.rank === 1 ? (
      <Crown size={14} className="text-[#D4AF37]" />
    ) : row.rank === 2 ? (
      <Medal size={14} className="text-[#AEB4BE]" />
    ) : row.rank === 3 ? (
      <Medal size={14} className="text-[#CD7F32]" />
    ) : null
  const completed = row.completedByPriority ?? {
    urgent: 0,
    high: 0,
    normal: 0,
    low: 0,
  }
  return (
    <div
      className={
        'grid grid-cols-[73px_281px_171px_141px_minmax(0,1fr)_101px] items-center px-4 py-3 gap-3 border-b border-[color:var(--color-rule)] last:border-b-0 ' +
        (highlight
          ? 'bg-[color:var(--color-accent-tint)]'
          : index % 2 === 0
            ? ''
            : 'bg-[color:var(--color-canvas)]')
      }
    >
      <span className="inline-flex items-center gap-1 font-display text-lg text-[color:var(--color-ink-strong)] tabular-nums">
        {rankIcon}
        {/* Unranked — a self-only viewer who has scored nothing in this
          *  window has no standing to show. Rendering `#{null}` printed a
          *  bare "#". */}
        {row.rank == null ? '—' : `#${row.rank}`}
      </span>
      <div className="flex items-center gap-3 min-w-0">
        <Avatar
          name={row.fullName}
          avatarUrl={row.avatarUrl ?? null}
          size="md"
        />
        <div className="min-w-0">
          <p className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate">
            {row.fullName}
            {highlight ? (
              <span className="ml-2 text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
                You
              </span>
            ) : null}
          </p>
          {/* Thin cut-off line separating the member name from their email. */}
          <div
            aria-hidden="true"
            className="my-0.5 h-px bg-[color:var(--color-border)]"
          />
          <p className="text-sm text-[color:var(--color-ink-muted)] truncate">
            {row.emailId}
          </p>
        </div>
      </div>
      <span className="text-sm text-[color:var(--color-ink-muted)] truncate">
        {row.departmentName ?? '—'}
      </span>
      <span className="text-sm font-medium text-[color:var(--color-ink)]">
        {/* Show the assigned Work Role's real name; fall back to the inert
         *  tier slug label only when no Work Role is assigned. */}
        {row.workRoleName ?? WORK_ROLE_LABEL[row.workRole] ?? 'Employee'}
      </span>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        {PRIORITY_ORDER.map(({ key, label }) => (
          <span
            key={key}
            className="inline-flex items-center gap-1.5"
            title={`${label} priority tasks completed: ${completed[key] ?? 0}`}
          >
            <span
              className="h-2 w-2 rounded-full shrink-0"
              style={{ background: `var(--color-priority-${key})` }}
              aria-hidden="true"
            />
            <span className="text-[color:var(--color-ink-muted)]">{label}</span>
            <span className="tabular-nums font-medium text-[color:var(--color-ink-strong)]">
              {completed[key] ?? 0}
            </span>
          </span>
        ))}
      </div>
      <span className="font-display text-xl text-[color:var(--color-ink-strong)] tabular-nums text-right inline-flex items-center justify-end gap-1">
        <Sparkles
          size={13}
          className="text-[color:var(--color-accent-strong)]"
        />
        {formatNumber(row.points)}
      </span>
    </div>
  )
}
